import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { BookOpen, Plus, Loader2, ExternalLink, FileText, Video, Image, Link2, Trash2, Search, MoreVertical, FolderOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryConfig = {
  general: { label: 'General', bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' },
  sop: { label: 'SOP', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  template: { label: 'Template', bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  guide: { label: 'Guide', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  tool: { label: 'Tool', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
};
const typeIcons = { link: Link2, document: FileText, video: Video, image: Image };

export default function ResourceLibraryPage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [form, setForm] = useState({ title: '', description: '', category: 'general', url: '', file_type: 'link' });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/resources`, { headers: { Authorization: `Bearer ${token}` } });
      setResources(res.data.resources);
    } catch { toast.error('Failed to load resources'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.title.trim()) { toast.error('Please enter a title'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/resources`, form, { headers: { Authorization: `Bearer ${token}` } });
      setResources([res.data.resource, ...resources]);
      setShowAdd(false);
      setForm({ title: '', description: '', category: 'general', url: '', file_type: 'link' });
      toast.success('Resource added!');
    } catch { toast.error('Failed to add resource'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/resources/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setResources(resources.filter(r => r.id !== id));
      toast.success('Resource deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = resources.filter(r => {
    if (catFilter !== 'all' && r.category !== catFilter) return false;
    if (search && !r.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = Object.entries(categoryConfig).reduce((acc, [key]) => {
    acc[key] = resources.filter(r => r.category === key).length;
    return acc;
  }, {});

  if (loading) return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader
          icon={BookOpen}
          title="UA Resource Library"
          subtitle="SOPs, templates, guides & tools"
          iconBg="bg-gradient-to-br from-cyan-500 to-blue-600 shadow-cyan-500/20"
          action={<Button data-testid="add-resource-btn" onClick={() => setShowAdd(true)} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20"><Plus size={18} className="mr-2" /> Add Resource</Button>}
        />

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search resources..." className="pl-9 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {['all', ...Object.keys(categoryConfig)].map(c => (
              <Button key={c} variant={catFilter === c ? 'default' : 'outline'} size="sm" onClick={() => setCatFilter(c)} className={catFilter === c ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white border-0' : 'border-[hsl(var(--border))]'}>
                {c === 'all' ? `All (${resources.length})` : `${categoryConfig[c].label} (${stats[c] || 0})`}
              </Button>
            ))}
          </div>
        </div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.length === 0 ? (
            <div className="col-span-full text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
              <FolderOpen size={48} className="mx-auto text-cyan-500/30 mb-4" />
              <p className="text-[hsl(var(--text-secondary))] font-medium">No resources found</p>
            </div>
          ) : filtered.map(res => {
            const cat = categoryConfig[res.category] || categoryConfig.general;
            const TypeIcon = typeIcons[res.file_type] || Link2;
            return (
              <div key={res.id} data-testid={`resource-${res.id}`} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 hover:border-cyan-500/20 transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className={`${cat.bg} ${cat.text} border ${cat.border} text-[10px]`}>{cat.label}</Badge>
                    <TypeIcon size={14} className="text-[hsl(var(--text-muted))]" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100"><MoreVertical size={14} /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <DropdownMenuItem onClick={() => handleDelete(res.id)} className="text-red-400"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h3 className="font-semibold text-sm mb-1 group-hover:text-cyan-400 transition-colors">{res.title}</h3>
                {res.description && <p className="text-xs text-[hsl(var(--text-muted))] mb-3 line-clamp-2">{res.description}</p>}
                {res.url && (
                  <a href={res.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:underline">
                    <ExternalLink size={12} /> Open Link
                  </a>
                )}
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-[hsl(var(--border))] text-[10px] text-[hsl(var(--text-muted))]">
                  <span>{res.user_name}</span>
                  <span>{new Date(res.created_at).toLocaleDateString('en-MY')}</span>
                </div>
              </div>
            );
          })}
        </div>

        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen size={18} className="text-cyan-400" /> Add Resource</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Title *</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Resource title" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} placeholder="Brief description..." className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm text-[hsl(var(--text-primary))] resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Category</label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      {Object.entries(categoryConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Type</label>
                  <Select value={form.file_type} onValueChange={v => setForm({...form, file_type: v})}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="link">Link</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">URL / Link</label>
                <Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={handleAdd} disabled={submitting} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white">{submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ResponsiveLayout>
  );
}
